import React, { FC, useState } from 'react';

import { Box, Icon, Link, Text, Heading } from '@lucid/components-react';
import dayjs from 'dayjs';
import { Drupal_NodePressReleases } from 'generated/drupal-graphql';
import { mqMin, LucidTheme } from '@lucid/theme';

interface PressReleasesProps {
  articles: Drupal_NodePressReleases[];
  showMore: string;
  showLess: string;
}

const PressReleases: FC<PressReleasesProps> = ({ articles, showLess, showMore }) => {
  const itemsPerRow = 4;
  const [itemsToShow, setItemsToShow] = useState(4);
  const firstOne = articles[0];
  const twoThroughRest = articles.slice(1, articles.length);
  const articlesBelow = twoThroughRest.slice(0, itemsToShow);

  return (
    <Box
      css={(theme: LucidTheme) => ({
        label: 'pressReleases',
        display: 'grid',
        gridTemplateColumns: 'repeat(12, 1fr)',
        gridTemplateRows: 'repeat(1, 1fr)',
        gridGap: theme.space[4],
        marginBottom: theme.space[10]
      })}
    >
      <Box
        css={(theme: LucidTheme) => ({
          gridColumn: '1 / span 12',
          padding: `${theme.space[8]}px ${theme.space[6]}px`,
          backgroundSize: '100% 100%',
          backgroundImage:
            'url("https://cdn-cashy-static-assets.lucidchart.com/lucidco/flexipage/newsroom/dev-imagex/company-announcements-background.png")',
          [mqMin.tabletLandscape]: {
            padding: `${theme.space[9]}px ${theme.space[8]}px ${theme.space[10]}px`,
            gridColumn: '1 / span 8'
          }
        })}
      >
        <Text
          css={(theme: LucidTheme) => ({
            color: theme.colors.white,
            margin: 0
          })}
          variant="labelS400"
        >
          {dayjs(firstOne!.fieldDate?.value!, 'MM-DD-YYYY').format(
            'MMMM D, YYYY'
          )}
        </Text>
        <Link to={firstOne.fieldSourceUrl!}>
          <Heading
            as="h3"
            css={(theme: LucidTheme) => ({
              color: theme.colors.white,
              width: '100%'
            })}
            variant="headlineXL"
          >
            {firstOne.title}
          </Heading>
        </Link>
      </Box>
      <Box
        css={{
          gridColumn: '1 / span 12',
          gridRow: '2',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between'
        }}
      >
        {articlesBelow.map(({ title, fieldDate, fieldSourceUrl }, i) => {
          const hasBorder = (i - 3) % 4 === 0;
          return (
            <Box
              key={title}
              css={(theme: LucidTheme) => ({
                borderBottom: `1px solid ${theme.colors.coolGrey[20]}`,
                padding: theme.space[4],
                paddingLeft: 0,
                flex: '0 1 100%',
                maxWidth: '100%',
                [mqMin.tabletLandscape]: {
                  borderBottom: 'none',
                  margin: `${theme.space[5]}px 0`,
                  padding: theme.space[4],
                  flex: '0 1 25%',
                  maxWidth: '25%',
                  borderRight: `${
                    hasBorder
                      ? 'none'
                      : `1px solid ${theme.colors.coolGrey[20]}`
                  }`
                }
              })}
            >
              <Text
                css={(theme: LucidTheme) => ({
                  margin: 0,
                  color: theme.colors.blue[60]
                })}
                variant="labelXS400"
              >
                {dayjs(fieldDate!.value!, 'MM-DD-YYYY').format('MMMM D, YYYY')}
              </Text>
              <Link to={fieldSourceUrl!}>
                <Text
                  css={{
                    width: '100%',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 6,
                    WebkitBoxOrient: 'vertical'
                  }}
                  variant="headlineM"
                >
                  {title}
                </Text>
              </Link>
            </Box>
          );
        })}
      </Box>
      <Box css={{ textAlign: 'center', gridColumn: '1 / span 12' }}>
        {twoThroughRest.length > itemsToShow && (
          <Box
            css={{
              display: 'flex',
              flexWrap: 'wrap',
              '&:hover': { cursor: 'pointer' }
            }}
            onClick={() => setItemsToShow(itemsToShow + itemsPerRow)}
          >
            <Text
              css={(theme: LucidTheme) => ({
                color: theme.colors.primary,
                flex: '0 1 100%',
                width: '100%',
                '&:hover': { cursor: 'pointer' }
              })}
              variant="headlineM"
            >
              {showMore}
            </Text>
            <Text
              css={{
                flex: '0 1 100%',
                width: '100%',
                '&:hover': { cursor: 'pointer' }
              }}
              variant="headlineM"
            >
              <Icon type="CarotDown" />
            </Text>
          </Box>
        )}
        {twoThroughRest.length < itemsToShow &&
          twoThroughRest.length > itemsPerRow && (
          <Box
            css={{
              display: 'flex',
              flexWrap: 'wrap',
              '&:hover': { cursor: 'pointer' }
            }}
            onClick={() => setItemsToShow(itemsPerRow)}
          >
            <Text
              css={(theme: LucidTheme) => ({
                color: theme.colors.primary,
                flex: '0 1 100%',
                width: '100%',
                '&:hover': { cursor: 'pointer' }
              })}
              variant="headlineM"
            >
              <Icon
                css={{
                  transform: 'rotate(180deg)'
                }}
                type="CarotDown"
              />
              <Box
                css={(theme: LucidTheme) => ({
                  marginTop: theme.space[3]
                })}
              >
                {showLess}
              </Box>
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PressReleases;
